import React, { useEffect, useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPartyById, deleteParty } from '../apiCalls/partyAPI';
import AuthContext, { CacheContext } from '../contexts';
import { NotificationContext } from '../contexts/NotificationContext';
import CountdownTimer from './CountdownTimer';
import LeaderBoard from './LeaderBoard';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import '../styles/ViewParty.css';


function ViewParty({ isShowtime }) {
    const { user } = useContext(AuthContext);
    const { cachedParties, setCachedParties, activeParty, setActiveParty } = useContext(CacheContext);
    const { showToast } = useContext(NotificationContext);
    const { partyId } = useParams();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const navigate = useNavigate();

    const isAdmin = user && activeParty && user.email === activeParty.adminEmail;

    useEffect(() => {
        const party = cachedParties.find(party => party.id === partyId);
        if (party) {
            setActiveParty(party);
        } else {
            getPartyById(partyId, user.credential)
                .then(party => {
                    setActiveParty(party);
                    setCachedParties([...cachedParties, party]);
                })
                .catch(error => {
                    if (!error.response) {
                        showToast('Unable to reach server. Please try again.', 'error');
                    } else {
                        showToast('Failed to load party. Please try again.', 'error');
                    }
                });
        }
    }, []);

    const handleDeleteConfirm = () => {
        deleteParty(activeParty.id, user.credential)
            .then(response => {
                if (response.ok) {
                    setCachedParties(cachedParties.filter(p => p.id !== activeParty.id));
                    setActiveParty(null);
                    navigate('/');
                } else {
                    setDeleteError('Failed to delete party. Please try again.');
                    setShowDeleteConfirm(false);
                }
            })
            .catch(error => {
                const msg = !error.response
                    ? 'Unable to reach server. Please try again.'
                    : 'Failed to delete party. Please try again.';
                setDeleteError(msg);
                showToast(msg, 'error');
                setShowDeleteConfirm(false);
            });
    };

    return (
        <div>
            <div className="view-party-title-card">
                <h1>Welcome to {activeParty?.partyName} {activeParty?.oscarYear}!</h1>
            </div>
            {
                !activeParty?.isHistory
                    ?
                    <>
                        <div className="view-party-countdown-card">
                            <h2>Countdown to Ceremony:</h2>
                            <CountdownTimer />
                            {
                                user && !activeParty?.isHistory && !isShowtime
                                    ?
                                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => navigate(`/my-picks/${activeParty.id}`)}
                                        >
                                            Set My Picks
                                        </button>
                                    </div>
                                    :
                                    <></>
                            }
                        </div>

                        <div className="leaderboard-container">
                            <div className="leaderboard-header">Leader Board</div>
                            <LeaderBoard partyId={activeParty?.id} isShowtime={isShowtime} />
                        </div>
                    </>
                    :
                    <div className="leaderboard-container">
                        <div className="leaderboard-header">Leader Board</div>
                        <LeaderBoard partyId={activeParty?.id} isShowtime={isShowtime} />
                    </div>
            }
            
            { isAdmin && (
                <div className="admin-actions-footer">
                    { deleteError && (
                        <div className="alert alert-danger" data-testid="delete-error">{deleteError}</div>
                    )}
                    <div className="admin-buttons-group">
                        <button
                            className="admin-icon-btn update-btn"
                            data-testid="update-party-btn"
                            onClick={() => navigate(`/party/update/${activeParty?.id}`)}
                            title="Edit Party"
                        >
                            <EditIcon sx={{ fontSize: '2rem' }} />
                            <span>Update</span>
                        </button>
                        { !showDeleteConfirm
                            ? (
                                <button
                                    className="admin-icon-btn delete-btn"
                                    data-testid="delete-party-btn"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    title="Delete Party"
                                >
                                    <DeleteIcon sx={{ fontSize: '2rem' }} />
                                    <span>Delete</span>
                                </button>
                            )
                            : (
                                <div className="delete-confirm" data-testid="delete-confirm-dialog">
                                    <p>Are you sure? This cannot be undone.</p>
                                    <button
                                        className="btn btn-danger"
                                        data-testid="confirm-delete-btn"
                                        onClick={handleDeleteConfirm}
                                    >
                                        Yes, Delete Party
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        data-testid="cancel-delete-btn"
                                        onClick={() => setShowDeleteConfirm(false)}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )
                        }
                    </div>
                </div>
            )}
        </div>
    );
}

export default ViewParty;

ViewParty.propTypes = {
    isShowtime: PropTypes.bool.isRequired
};
